import { useState, useEffect } from 'react'
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Text,
  Badge,
  Spinner,
  Button,
} from '@fluentui/react-components'
import {
  CheckmarkCircle24Filled,
  DismissCircle24Filled,
  Warning24Filled,
  ArrowLeft24Regular,
} from '@fluentui/react-icons'
import { useNavigate } from 'react-router-dom'

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingVerticalXXL,
    fontFamily: tokens.fontFamilyBase,
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
    textAlign: 'center',
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalS,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: tokens.spacingHorizontalL,
    maxWidth: '1200px',
    margin: '0 auto',
  },
  card: {
    padding: tokens.spacingVerticalM,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  overallStatus: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalXL,
    borderRadius: tokens.borderRadiusMedium,
  },
  healthy: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  degraded: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
  },
  unhealthy: {
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
})

interface ServiceHealth {
  name: string
  status: 'healthy' | 'degraded' | 'unhealthy' | 'checking'
  latency?: number
  message?: string
  endpoint?: string
}

const AZURE_RESOURCES = {
  cosmosDb: 'https://cosmos-librarian-dev.documents.azure.com:443/',
  storage: 'https://stlibrarian0fde.blob.core.windows.net/',
  keyVault: 'https://kv-librarian-dev.vault.azure.net/',
  aiSearch: 'https://search-librarian-dev.search.windows.net/',
  openAi: 'https://oai-librarian-dev.openai.azure.com/',
}

export function HealthCheck() {
  const styles = useStyles()
  const navigate = useNavigate()
  const [services, setServices] = useState<ServiceHealth[]>([
    { name: 'Frontend App', status: 'checking', endpoint: window.location.origin },
    { name: 'Cosmos DB', status: 'checking', endpoint: AZURE_RESOURCES.cosmosDb },
    { name: 'Blob Storage', status: 'checking', endpoint: AZURE_RESOURCES.storage },
    { name: 'Key Vault', status: 'checking', endpoint: AZURE_RESOURCES.keyVault },
    { name: 'AI Search', status: 'checking', endpoint: AZURE_RESOURCES.aiSearch },
    { name: 'Azure OpenAI', status: 'checking', endpoint: AZURE_RESOURCES.openAi },
  ])
  const [lastCheck, setLastCheck] = useState<Date | null>(null)

  useEffect(() => {
    checkHealth()
    const interval = setInterval(checkHealth, 30000) // Refresh every 30s
    return () => clearInterval(interval)
  }, [])

  async function checkHealth() {
    // Frontend is always healthy if this code runs
    const frontendHealth: ServiceHealth = {
      name: 'Frontend App',
      status: 'healthy',
      latency: 0,
      message: 'React app running',
      endpoint: window.location.origin,
    }

    // For backend services, we'd normally call a /health endpoint
    // Since we're a static app with no backend yet, simulate pending checks
    const backendServices: ServiceHealth[] = [
      {
        name: 'Cosmos DB',
        status: 'degraded',
        message: 'Backend API not deployed - cannot verify',
        endpoint: AZURE_RESOURCES.cosmosDb,
      },
      {
        name: 'Blob Storage',
        status: 'degraded',
        message: 'Backend API not deployed - cannot verify',
        endpoint: AZURE_RESOURCES.storage,
      },
      {
        name: 'Key Vault',
        status: 'degraded',
        message: 'Backend API not deployed - cannot verify',
        endpoint: AZURE_RESOURCES.keyVault,
      },
      {
        name: 'AI Search',
        status: 'degraded',
        message: 'Backend API not deployed - cannot verify',
        endpoint: AZURE_RESOURCES.aiSearch,
      },
      {
        name: 'Azure OpenAI',
        status: 'degraded',
        message: 'Backend API not deployed - cannot verify',
        endpoint: AZURE_RESOURCES.openAi,
      },
    ]

    setServices([frontendHealth, ...backendServices])
    setLastCheck(new Date())
  }

  const overallStatus = services.some((s) => s.status === 'unhealthy')
    ? 'unhealthy'
    : services.some((s) => s.status === 'degraded')
      ? 'degraded'
      : services.every((s) => s.status === 'healthy')
        ? 'healthy'
        : 'checking'

  const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
      case 'healthy':
        return <CheckmarkCircle24Filled primaryFill={tokens.colorPaletteGreenForeground1} />
      case 'degraded':
        return <Warning24Filled primaryFill={tokens.colorPaletteYellowForeground1} />
      case 'unhealthy':
        return <DismissCircle24Filled primaryFill={tokens.colorPaletteRedForeground1} />
      default:
        return <Spinner size="tiny" />
    }
  }

  return (
    <div className={styles.container}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Button
          icon={<ArrowLeft24Regular />}
          appearance="subtle"
          onClick={() => navigate('/')}
          style={{ marginBottom: tokens.spacingVerticalM }}
        >
          Back to Dashboard
        </Button>
      </div>
      <div className={styles.header}>
        <Text className={styles.title}>The Librarian - Solution Health</Text>
        <Text className={styles.subtitle} block>
          End-to-end system health • {lastCheck?.toLocaleString() ?? 'Checking...'}
        </Text>
      </div>

      <div
        className={`${styles.overallStatus} ${
          overallStatus === 'healthy'
            ? styles.healthy
            : overallStatus === 'degraded'
              ? styles.degraded
              : styles.unhealthy
        }`}
      >
        <StatusIcon status={overallStatus} />
        <Text weight="semibold" size={500}>
          Overall Status: {overallStatus.toUpperCase()}
        </Text>
      </div>

      <div className={styles.grid}>
        {services.map((service) => (
          <Card key={service.name} className={styles.card}>
            <CardHeader
              header={<Text weight="semibold">{service.name}</Text>}
              description={
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  {service.endpoint}
                </Text>
              }
            />
            <div className={styles.statusRow}>
              <StatusIcon status={service.status} />
              <Badge
                appearance="filled"
                color={
                  service.status === 'healthy'
                    ? 'success'
                    : service.status === 'degraded'
                      ? 'warning'
                      : service.status === 'unhealthy'
                        ? 'danger'
                        : 'informative'
                }
              >
                {service.status}
              </Badge>
              {service.latency !== undefined && (
                <Text size={200}>{service.latency}ms</Text>
              )}
            </div>
            {service.message && (
              <Text size={200} style={{ marginTop: tokens.spacingVerticalS, display: 'block' }}>
                {service.message}
              </Text>
            )}
          </Card>
        ))}
      </div>

      <div className={styles.metaRow} style={{ maxWidth: '1200px', margin: '24px auto 0' }}>
        <Text size={200}>Environment: DEV</Text>
        <Text size={200}>Region: eastus2</Text>
        <Text size={200}>Build: {import.meta.env.VITE_BUILD_SHA ?? 'local'}</Text>
      </div>
    </div>
  )
}
